import { gql, useQuery } from '@apollo/client';
import intersection from 'lodash/intersection';

import { EUserGroupRole } from '@/client/legacy-shared-modules/user-group-link/user-group-role';
import { usePermissions } from '@/hooks/usePermissions';

import { useEventEditPermissions_CurrentUser } from './graphql-types/useEventEditPermissions_CurrentUser';
import { useEventEditPermissions_EventFragment } from './graphql-types/useEventEditPermissions_EventFragment';

const CURRENT_USER = gql`
  query useEventEditPermissions_CurrentUser {
    currentUser {
      _id
      visibleGroupLinks {
        _id
        userGroupRole
        groupId
      }
    }
  }
`;

export const useEventEditPermissions = (options: {
  event?: useEventEditPermissions_EventFragment;
}): {
  isLoading: boolean;
  eventEditHref: string;
} => {
  const userQuery = useQuery<useEventEditPermissions_CurrentUser>(CURRENT_USER);
  const permissions = usePermissions();

  if (!options.event) {
    return {
      isLoading: false,
      eventEditHref: '',
    };
  }

  const canManageOrganizationSponsoredEvents =
    (options.event.__typename === 'SimpleEventV2' &&
      options.event.hostOrganization?.currentUserOrganizationPermission
        .canManageOrganizationSponsoredEvents) ||
    false;

  const adminEditLinkBase =
    options.event.__typename === 'ConferenceEventV2' ? 'conference' : 'event';

  // In case the current event is a group event AND current user is a group admin.
  // We need to get a valid groupId hosting that group event.
  const getGroupIdHostingCurrentGroupEvent = () => {
    const groupIdsHostingCurrentEvent =
      options.event?.visibility.groupIds || [];

    const groupIdsCurrentUserIsGroupAdminOf =
      userQuery.data?.currentUser?.visibleGroupLinks
        .filter((group) => group.userGroupRole === EUserGroupRole.GroupAdmin)
        .map((group) => group.groupId) || [];

    return intersection(
      groupIdsHostingCurrentEvent,
      groupIdsCurrentUserIsGroupAdminOf
    )[0];
  };

  const groupId =
    options.event.isCurrentUserAudienceGroupAdmin &&
    getGroupIdHostingCurrentGroupEvent();

  return {
    isLoading: userQuery.loading,
    eventEditHref: permissions.canDoAdminStuffAndAuthenticated
      ? `/admin/${adminEditLinkBase}/${options.event._id}/overview`
      : canManageOrganizationSponsoredEvents
      ? `/my-company/events/${options.event._id}`
      : groupId
      ? `/community/groups/${groupId}/events/${options.event._id}/overview`
      : '',
  };
};

useEventEditPermissions.eventFragment = gql`
  fragment useEventEditPermissions_EventFragment on EventV2 {
    _id
    isCurrentUserAudienceGroupAdmin
    visibility {
      groupIds
    }
    ... on SimpleEventV2 {
      _id
      hostOrganization {
        _id
        currentUserOrganizationPermission {
          canManageOrganizationSponsoredEvents
        }
      }
    }
    ... on ConferenceEventV2 {
      _id
    }
  }
`;
